import React, { useState } from 'react';
import { Zap, ArrowRight, Check, MessageCircle, Clock } from 'lucide-react';
import { Button } from './ui/button';
import BookingCalendar from './BookingCalendar';

const VIDEO_URL = 'https://customer-assets-gfyr7b9c.emergentagent.net/job_4d2f52c7-22cd-4150-9059-ad99d56ca7fb/artifacts/btq5zuyn_XiaoYing_Video_1790867839342_HD.mp4';

const SHARE_MSG = `Tight legs from running? Dublin Holistic Massage specialises in sports recovery — deep calf & leg work that releases tight muscles fast.\n\nSave 15% when you pay in full online!\n\nBonus: book Mon-Fri between 12pm and 3pm and get 10% off automatically with the Lunchtime Deal instead.\n\nSee it in action & book: https://www.dublinholisticmassages.com/sports-recovery`;

const BENEFITS = [
  'Targeted calf & hamstring release for runners',
  'Eases tightness from sprinting, marathons & training',
  'Trusted by competing Masters athletes',
];

const SportsRecoverySpotlight = () => {
  const [bookingOpen, setBookingOpen] = useState(false);

  return (
    <>
      <section className="py-16 bg-luxury-green relative overflow-hidden" data-testid="sports-recovery-spotlight">
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-luxury-gold/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-luxury-gold/5 rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-luxury-gold/15 text-luxury-gold px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider mb-6">
                <Zap size={14} />
                Sports Recovery
              </div>

              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-alabaster mb-4 leading-tight">
                Tight legs
                <span className="block italic text-luxury-gold/80 mt-1">from running?</span>
              </h2>

              <p className="text-alabaster-200/70 font-light mb-6 max-w-md leading-relaxed">
                Watch Leslie release Dr. Michael's calves — tight from sprint training.
                Our therapists know exactly where runners hold tension and how to melt it away,
                so you recover faster and run lighter.
              </p>

              <ul className="space-y-2 mb-6">
                {BENEFITS.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-alabaster-200/80 text-sm">
                    <Check size={15} className="text-luxury-gold mt-0.5 flex-shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              <div className="inline-flex items-center gap-2 bg-luxury-green-800 border border-luxury-gold/25 rounded-full px-4 py-2 mb-6" data-testid="sports-lunchtime-hint">
                <Clock size={14} className="text-luxury-gold" />
                <span className="text-xs text-alabaster-200/80">
                  Run at lunch? Book Mon&ndash;Fri 12&ndash;3pm and save <strong className="text-luxury-gold">10%</strong> automatically
                </span>
              </div>

              <div className="flex items-center gap-3">
                <Button
                  onClick={() => setBookingOpen(true)}
                  className="bg-luxury-gold hover:bg-luxury-gold/90 text-luxury-green rounded-full py-6 px-8 text-sm uppercase tracking-wider font-semibold group transition-all"
                  data-testid="sports-recovery-book-btn"
                >
                  Book Sports Recovery
                  <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button
                  onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(SHARE_MSG)}`, '_blank')}
                  className="bg-luxury-green-600 hover:bg-luxury-green-500 text-white rounded-full w-12 h-12 p-0 flex items-center justify-center"
                  data-testid="sports-recovery-share-btn"
                  title="Share with a running buddy on WhatsApp"
                >
                  <MessageCircle size={20} />
                </Button>
              </div>

              <a
                href="/sports-recovery"
                className="inline-flex items-center gap-1.5 mt-4 text-luxury-gold/90 hover:text-luxury-gold text-xs uppercase tracking-wider font-medium underline underline-offset-4"
                data-testid="sports-recovery-page-link"
              >
                View the full runners' page
                <ArrowRight size={13} />
              </a>
            </div>

            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-black/40 border border-luxury-gold/20">
                <video
                  src={VIDEO_URL}
                  controls
                  playsInline
                  preload="metadata"
                  className="w-full max-h-[420px] object-cover bg-black"
                  data-testid="sports-recovery-video"
                />
                <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-sm text-white text-[11px] px-3 py-1 rounded-full pointer-events-none">
                  Leslie releasing sprint-tight calves
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <BookingCalendar isOpen={bookingOpen} onClose={() => setBookingOpen(false)} />
    </>
  );
};

export default SportsRecoverySpotlight;
